import React, { useState, useEffect, useCallback } from "react";
import ReactDom from "react-dom";
import axios from "axios";
import { BookingEngine, roomData } from "../../hotel_integration_modules/index";
import GuestbookLight from "../UI/Icons/GuestbookLight";
import {
  numOfHotels,
  guestbookSites,
  environment,
  honeybadger,
} from "../../settings/IntegrationConstants";
import { notifyHoneybadger } from "../../hotel_integration_modules/utilities/utils";
import translations from "../../hotel_integration_modules/utilities/translations";
import I18n from "../../translations/i18n";
import styled from "styled-components";

type BackgroundPrimaryColorProp = {
  backgroundPrimaryColor: boolean;
};

const StyledConfirmationContainer = styled.div<BackgroundPrimaryColorProp>`
  font-family: ${({ theme }) => theme.primaryFont};
  display: flex !important;
  background: ${({ theme, backgroundPrimaryColor }) =>
    backgroundPrimaryColor ? theme.primaryColor : "#ffffff"} !important;
  color: ${({ backgroundPrimaryColor }) =>
    backgroundPrimaryColor ? "#ffffff" : "#343638"} !important;
  padding: 16px !important;
  ${({ backgroundPrimaryColor }) =>
    !backgroundPrimaryColor && "border: 1px solid #e4e4e4;"
  }
`;

const LogoContainer = styled.div<BackgroundPrimaryColorProp>`
  display: flex !important;
  flex-direction: column !important;
  justify-content: center !important;
  filter: ${({ backgroundPrimaryColor }) =>
    backgroundPrimaryColor ? "none" : "invert(1)"}  !important;

  svg {
    height: 45px !important;
    width: 45px !important;
  }
`;

const ConfirmationText = styled.div<BackgroundPrimaryColorProp>`
  text-align: left !important;
  margin-left: 16px !important;
  line-height: 20px !important;
  font-size: 13px !important;
  font-weight: 400 !important;
  a {
    color: ${({ backgroundPrimaryColor }) =>
    backgroundPrimaryColor ? "#ffffff" : "#343638"} !important;
    text-decoration: underline !important;
    border-bottom: none !important;
  }
`;

type ConfirmationMessageProps = {
  bookingEngine: BookingEngine;
  currency: string;
  locale: string;
};

const ConfirmationMessage = ({
  bookingEngine,
  currency,
  locale,
}: ConfirmationMessageProps) => {
  const [showConfirmMessage, setShowConfirmMessage] = useState(false);
  const confirmationTarget = bookingEngine.getConfirmationTarget();
  const rewards15 = bookingEngine.getRewards(0.15);
  const rewards5 = bookingEngine.getRewards(0.05);

  const submitReservation = useCallback(
    async (room: roomData) => {
      // Change submission domain to theguestbook.com for Synxis AB tests
      const submissionURL = (bookingEngine.name == 'synxis_gtm') ?
        `${guestbookSites[environment as string].guestbook.url}` :
        `${guestbookSites[environment as string].rewardsdash.url}`;

      room.source = !!sessionStorage.getItem('tgb_app') ? 'tgb_app' : 'web';
      await axios
        .post(
          submissionURL + '/api/v1/submit_booking_engine_reservation',
          room,
          {
            headers: {
              "Content-Type": "application/json",
              "Access-Control-Allow-Origin": "*",
            },
          }
        )
        .then((response) => {
          if (response.status === 200) {
            if (
              sessionStorage.getItem("isMemberOrEnrolled") === "true" ||
              room.agreed_enrollment_checked === "true" ||
              response.data.success ||
              response.data.success === "true"
            ) {
              if (sessionStorage.getItem("rateExcluded") !== "true") {
                setShowConfirmMessage(true);
              }
            }
          } else {
            console.log(response);
          }
        })
        .catch((error) => {
          console.log(error);
          honeybadger.notify(`Submit reservation error: ${error}`, {
            tags: "new_bei, confirmation_page",
            context: {
              bookingEngine: `${bookingEngine.name}`,
              property: `${bookingEngine.hotel.name}`,
            },
          });
        });
    },
    [bookingEngine.hotel.name, bookingEngine.name]
  );

  useEffect(() => {
    const submitBookingReservation = async () => {
      const resData = JSON.parse(
        sessionStorage.getItem("gbBookingDetails") as string
      );

      // For multiple reservations, calling 3+ submit_booking_engine_reservation simultaneously causes 500 and may drop some reservations
      // add await submitReservations to preventing server overload
      for (let i = 0; i < resData.length; i++) {
        const room = resData[i];
        // Most of the time, the confirmation number(s) won't show until the confirmation page. If so grab the confirmation here
        if (room.confirmation_number === "") {
          room.confirmation_number = bookingEngine.getConfirmationNumber()[i];
        }
        if (room.email === "") {
          notifyHoneybadger({ name: "CustomError", message: "Email not populated" }, "checkEmailBeforeSubmission()");
        }
        room.rate_excluded = sessionStorage.getItem("rateExcluded") === "true";
        await submitReservation(room);
      }
    }

    submitBookingReservation();
  }, [bookingEngine, submitReservation]);

  if (showConfirmMessage && rewards15 !== "0.00") {
    return ReactDom.createPortal(
      <StyledConfirmationContainer
        backgroundPrimaryColor={bookingEngine.hotel.desktopEnrollmentPrimaryColor}
        className="confirmation-container"
      >
        <LogoContainer
          backgroundPrimaryColor={bookingEngine.hotel.desktopEnrollmentPrimaryColor}
          className="logo-container"
        >
          <GuestbookLight />
        </LogoContainer>
        <ConfirmationText
          backgroundPrimaryColor={bookingEngine.hotel.desktopEnrollmentPrimaryColor}
          className="confirmation-text"
        >
          {`${translations(
            "confirmation_messaging",
            "for_this_stay",
            locale
          )}: `}{" "}
          <span>
            {currency +
              I18n.t(
                `views.hotel_integration.components.enrollment_messaging.cash_back`,
                {
                  locale: `${locale}`,
                  five_rewards: rewards5,
                }
              )}
          </span>
          ,&nbsp;
          <span>
            {currency +
              I18n.t(
                `views.hotel_integration.components.enrollment_messaging.cash_for_a_cause`,
                {
                  locale: `${locale}`,
                  five_rewards: rewards5,
                }
              )}
          </span>
          , {`${translations("enrollment_messaging", "or", locale)} `}
          <span>
            {currency +
              I18n.t(
                `views.hotel_integration.components.enrollment_messaging.cash_forward`,
                {
                  locale: `${locale}`,
                  fifteen_rewards: rewards15,
                }
              )}
          </span>{" "}
          {`${translations(
            "enrollment_messaging",
            "towards_a_future_stay",
            locale
          )} `}
          <a
            className="partner-hotels"
            href={`https://theguestbook.com/destinations/guestbook`}
            target="_blank"
            rel="noreferrer"
          >
            {I18n.t(
              `views.hotel_integration.components.enrollment_messaging.partner_hotels`,
              {
                locale: `${locale}`,
                num_of_hotels: numOfHotels,
              }
            )}
          </a>
          .
        </ConfirmationText>
      </StyledConfirmationContainer>,
      confirmationTarget as Element
    );
  } else {
    console.log("Confirmation Message Error: showConfirmMessage = " + showConfirmMessage + " rewards15 = " + rewards15)
    return null;
  }
};

export default ConfirmationMessage;
